<template>
  <div id="app">
    <el-container>
      <el-header>
        <div class="header-content">
          <h1>AI 墨色生成 - 智能书法创作平台</h1>
        </div>
      </el-header>
      <el-main>
        <div class="main-layout">
          <!-- 左侧：风格选择 -->
          <div class="left-sidebar">
            <div class="style-selection-sidebar">
              <h3 class="sidebar-title"><i class="el-icon-brush"></i> 选择风格</h3>
              <div class="style-list">
                <div 
                  v-for="style in styleOptions" 
                  :key="style.value"
                  class="style-item-vertical"
                  :class="{ active: styleType === style.value }"
                  @click="styleType = style.value"
                >
                  <div class="style-image-container-vertical">
                    <img :src="style.image" :alt="style.label" class="style-image-vertical" />
                  </div>
                  <div class="style-label-vertical">{{ style.label }}</div>
                  <i v-if="styleType === style.value" class="el-icon-success style-check-vertical"></i>
                </div>
              </div>
              
              <!-- 生成按钮 -->
              <!-- <div class="generate-section-sidebar">
                <el-button 
                  v-if="!generating"
                  type="primary" 
                  size="medium" 
                  icon="el-icon-magic-stick"
                  @click="generateImage"
                  :disabled="!hasDrawing"
                  style="width: 100%;"
                >
                  开始生成
                </el-button>
                <el-button 
                  v-else
                  type="danger" 
                  size="medium" 
                  icon="el-icon-close"
                  @click="cancelGenerate"
                  style="width: 100%;"
                >
                  取消生成
                </el-button>
              </div> -->
            </div>
          </div>
          
          <!-- 中间：画板 -->
          <div class="center-canvas">
            <el-card shadow="hover" class="canvas-card">
              <div slot="header">
                <span><i class="el-icon-edit"></i> 请在下方米字格中手写一个汉字</span>
              </div>
              
              <div class="canvas-wrapper">
                <!-- 显示画布 -->
                <canvas
                  v-show="!resultImage"
                  ref="drawingCanvas"
                  @mousedown="startDrawing"
                  @mousemove="draw"
                  @mouseup="stopDrawing"
                  @mouseleave="stopDrawing"
                  @touchstart="handleTouchStart"
                  @touchmove="handleTouchMove"
                  @touchend="handleTouchEnd"
                  class="drawing-canvas"
                ></canvas>
                
                <!-- 显示生成结果 -->
                <div v-if="resultImage" class="result-image-container">
                  <el-image
                    :src="resultImage"
                    fit="contain"
                    class="result-image"
                    :preview-src-list="[resultImage]"
                  >
                    <div slot="error" class="image-error-slot">
                      <i class="el-icon-picture-outline"></i>
                    </div>
                  </el-image>
                </div>
              </div>
              
              <div class="canvas-actions">
                <el-button 
                  v-if="!resultImage"
                  size="small" 
                  icon="el-icon-delete" 
                  @click="clearCanvas"
                >清除画布</el-button>
                
                <el-button 
                  v-else
                  size="small" 
                  icon="el-icon-refresh-left" 
                  @click="resetToCanvas"
                >重新绘制</el-button>
                
                <el-button 
                  size="small" 
                  type="primary"
                  icon="el-icon-magic-stick" 
                  @click="generateImage"
                  :disabled="!hasDrawing || generating"
                >生成图片</el-button>
                
                <!-- <el-button 
                  v-if="resultImage"
                  size="small" 
                  icon="el-icon-download" 
                  @click="downloadResult"
                >下载图片</el-button> -->
              </div>
              
              <div class="canvas-footer">
                <p class="copyright-text">© 2025 AI 墨色生成平台 | 代码：杨子龙、许思源 | 指导老师：叶晨</p>
              </div>
            </el-card>
          </div>
          
          <!-- 右侧：历史记录 -->
          <div class="right-sidebar">
            <div class="history-panel">
              <div class="history-header">
                <h3><i class="el-icon-time"></i> 生成历史 <span v-if="historyIsMock" class="history-mock-tip">（演示数据）</span></h3>
                <el-button 
                  v-if="historyTotal > 0"
                  type="text" 
                  size="mini"
                  @click="clearHistory"
                  style="color: #C97A5E;"
                >清空</el-button>
              </div>
              
              <div v-if="historyList.length === 0" class="history-empty">
                <i class="el-icon-picture-outline-round"></i>
                <p>暂无生成历史</p>
              </div>
              
              <template v-else>
                <div class="history-list">
                  <div 
                    v-for="(item, index) in historyList" 
                    :key="item.id || index"
                    class="history-item"
                    @click="viewHistoryImage(item)"
                  >
                    <el-image
                      :src="item.image"
                      fit="cover"
                      class="history-thumbnail"
                    >
                      <div slot="error" class="history-image-slot">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                    </el-image>
                    <div class="history-info">
                      <div class="history-time">{{ formatTime(item.timestamp) }}</div>
                      <div class="history-style">{{ getStyleName(item.styleType) }}</div>
                    </div>
                    <el-button
                      type="text"
                      icon="el-icon-delete"
                      size="mini"
                      class="history-delete"
                      @click.stop="deleteHistoryItem(item)"
                      title="删除"
                    ></el-button>
                  </div>
                </div>
                <el-pagination
                  v-if="historyTotal > historyPageSize"
                  small
                  layout="prev, pager, next"
                  :current-page="historyPage"
                  :page-size="historyPageSize"
                  :total="historyTotal"
                  @current-change="onHistoryPageChange"
                  class="history-pagination"
                />
              </template>
            </div>
          </div>
        </div>
      </el-main>
    </el-container>
    
    <!-- 生成进度弹窗 -->
    <el-dialog
      :visible.sync="showProgressDialog"
      width="500px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      center
      class="progress-dialog"
    >
      <div slot="title" class="dialog-title">
        <i class="el-icon-loading"></i>
        <span>正在生成墨迹作品</span>
      </div>
      
      <div class="progress-content">
        <!-- 等待状态：显示等待人数 -->
        <div v-if="userTaskStatus === 'waiting'" class="waiting-content">
          <div class="waiting-icon">
            <i class="el-icon-time"></i>
          </div>
          <div class="waiting-message">
            <div class="waiting-title">任务正在排队中...</div>
            <div class="waiting-count">前方等待的用户还有 {{ tasksAhead }} 个</div>
          </div>
        </div>
        
        <!-- 执行状态：显示进度条 -->
        <div v-else>
          <div class="progress-stage">{{ currentStage }}</div>
          <el-progress 
            :percentage="progressPercentage" 
            :stroke-width="20"
            :color="progressColor"
          ></el-progress>
          <div class="progress-tip">{{ progressTip }}</div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button 
          type="danger" 
          size="medium"
          icon="el-icon-close"
          @click="cancelGenerate"
        >取消生成</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      // uploadUrl 使用相对路径，axios 会自动拼接 baseURL
      uploadUrl: '/image/upload',
      fileList: [],
      uploadedFileName: '',
      uploadedImageUrl: '',
      styleType: 'dry',
      styleOptions: [
        { value: 'strong', label: '浓', image: `${process.env.BASE_URL}styles/strong.png` },
        { value: 'light', label: '淡', image: `${process.env.BASE_URL}styles/light.png` },
        { value: 'dry', label: '枯', image: `${process.env.BASE_URL}styles/dry.png` },
        { value: 'wet', label: '湿', image: `${process.env.BASE_URL}styles/wet.png` }  
      ],
      params: {
        strength: 0.9,
        guidanceScale: 7.0,
        controlnetScale: 1.0,
        numInferenceSteps: 50
      },
      generating: false,
      resultImage: '',
      cancelTokenSource: null,
      historyList: [],
      historyPage: 1,
      historyPageSize: 10,
      historyTotal: 0,
      /** 后端未分页时一次性返回的全量数据，前端自己做分页展示 */
      fullHistoryCache: [],
      /** 后端不可用时使用的本地 mock 数据，用于测试历史与分页 */
      mockHistoryData: [],
      historyIsMock: false,
      isDrawing: false,
      hasDrawing: false,
      canvasContext: null,
      lastX: 0,
      lastY: 0,
      // 进度相关
      showProgressDialog: false,
      progressPercentage: 0,
      currentStage: '铺纸研墨',
      progressTip: '准备生成环境...',
      progressTimer: null,
      queueMonitorTimer: null, // 队列监控定时器
      currentTaskId: null, // 当前用户的任务ID
      userTaskStatus: 'no_task', // 当前用户任务状态: no_task, waiting, executing
      tasksAhead: 0, // 前方等待的任务数
      stages: [
        { name: '铺纸研墨', tip: '准备生成环境...', range: [0, 25] },
        { name: '意在笔先', tip: '构思布局...', range: [25, 50] },
        { name: '挥毫泼墨', tip: '正在生成墨迹...', range: [50, 85] },
        { name: '画龙点睛', tip: '完善细节...', range: [85, 100] }
      ]
    }
  },
  computed: {
    progressColor() {
      if (this.progressPercentage < 25) {
        return '#909399' // 灰色 - 铺纸研墨
      } else if (this.progressPercentage < 50) {
        return '#E6A23C' // 橙色 - 意在笔先
      } else if (this.progressPercentage < 85) {
        return '#409EFF' // 蓝色 - 挥毫泼墨
      } else {
        return '#67C23A' // 绿色 - 画龙点睛
      }
    }
  },
  mounted() {
    this.loadHistory()
    this.initCanvas()
    // 启动队列信息监控
    this.startQueueMonitor()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.stopProgressSimulation()
    this.stopQueueMonitor()
  },
  methods: {
    resetUpload() {
      this.uploadedFileName = ''
      this.uploadedImageUrl = ''
      this.fileList = []
      this.resultImage = ''
      this.clearCanvas()
    },
    initCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.drawingCanvas
        if (!canvas) {
          setTimeout(() => this.initCanvas(), 100)
          return
        }
        
        // 画布实际分辨率设为正方形
        const canvasSize = 600
        canvas.width = canvasSize
        canvas.height = canvasSize
        
        // 计算显示大小（正方形，尽量占满容器）
        const container = canvas.parentElement
        if (container) {
          const containerWidth = container.clientWidth
          const containerHeight = container.clientHeight
          // 为按钮区域预留空间，减少画布最大尺寸
          const size = Math.min(containerWidth, containerHeight -10, 600)
          canvas.style.width = size + 'px'
          canvas.style.height = size + 'px'
        }
        
        this.canvasContext = canvas.getContext('2d')
        if (this.canvasContext) {
          // 绘制米字格背景
          this.drawRiceGrid(canvas.width, canvas.height)
          // 设置绘制样式
          this.canvasContext.strokeStyle = '#000000'
          this.canvasContext.lineWidth = 4
          this.canvasContext.lineCap = 'round'
          this.canvasContext.lineJoin = 'round'
        }
        
        window.addEventListener('resize', this.handleResize)
      })
    },
    handleResize() {
      this.$nextTick(() => {
        const canvas = this.$refs.drawingCanvas
        if (!canvas) return
        
        const container = canvas.parentElement
        if (container) {
          const containerWidth = container.clientWidth
          const containerHeight = container.clientHeight
          // 为按钮区域预留空间，减少画布最大尺寸
          const size = Math.min(containerWidth, containerHeight - 10, 600)
          canvas.style.width = size + 'px'
          canvas.style.height = size + 'px'
        }
      })
    },
    getCanvasCoordinates(e) {
      const canvas = this.$refs.drawingCanvas
      if (!canvas) return { x: 0, y: 0 }
      
      const rect = canvas.getBoundingClientRect()
      const scaleX = canvas.width / rect.width
      const scaleY = canvas.height / rect.height
      
      if (e.touches && e.touches.length > 0) {
        // 触摸事件
        return {
          x: (e.touches[0].clientX - rect.left) * scaleX,
          y: (e.touches[0].clientY - rect.top) * scaleY
        }
      } else {
        // 鼠标事件
        return {
          x: (e.clientX - rect.left) * scaleX,
          y: (e.clientY - rect.top) * scaleY
        }
      }
    },
    startDrawing(e) {
      e.preventDefault()
      this.isDrawing = true
      const coords = this.getCanvasCoordinates(e)
      this.lastX = coords.x
      this.lastY = coords.y
    },
    draw(e) {
      if (!this.isDrawing || !this.canvasContext) return
      
      e.preventDefault()
      const coords = this.getCanvasCoordinates(e)
      
      // 确保使用黑色笔触
      this.canvasContext.strokeStyle = '#000000'
      this.canvasContext.lineWidth = 4
      this.canvasContext.lineCap = 'round'
      this.canvasContext.lineJoin = 'round'
      
      this.canvasContext.beginPath()
      this.canvasContext.moveTo(this.lastX, this.lastY)
      this.canvasContext.lineTo(coords.x, coords.y)
      this.canvasContext.stroke()
      
      this.lastX = coords.x
      this.lastY = coords.y
      this.hasDrawing = true
    },
    stopDrawing(e) {
      if (this.isDrawing) {
        this.isDrawing = false
      }
    },
    // 触摸事件处理
    handleTouchStart(e) {
      this.startDrawing(e)
    },
    handleTouchMove(e) {
      this.draw(e)
    },
    handleTouchEnd(e) {
      this.stopDrawing(e)
    },
    clearCanvas() {
      const canvas = this.$refs.drawingCanvas
      if (!canvas || !this.canvasContext) return
      
      // 重新绘制米字格背景
      this.drawRiceGrid(canvas.width, canvas.height)
      this.hasDrawing = false
      this.uploadedImageUrl = ''
      this.uploadedFileName = ''
    },
    drawRiceGrid(width, height) {
      if (!this.canvasContext) return
      
      const ctx = this.canvasContext
      
      // 1. 绘制背景（米白色纸张效果）
      ctx.fillStyle = '#FEF9E7' // 浅米黄色，类似宣纸
      ctx.fillRect(0, 0, width, height)
      
      // 2. 绘制外边框
      ctx.strokeStyle = '#D4AF37' // 金色边框，类似字帖
      ctx.lineWidth = 2
      ctx.strokeRect(10, 10, width - 20, height - 20)
      
      // 3. 绘制米字格线条（浅灰色虚线）
      ctx.strokeStyle = '#E8E8E8' // 浅灰色辅助线
      ctx.lineWidth = 1
      ctx.setLineDash([5, 5]) // 设置虚线样式：5px实线，5px空白
      
      const centerX = width / 2
      const centerY = height / 2
      
      // 绘制中心横线
      ctx.beginPath()
      ctx.moveTo(10, centerY)
      ctx.lineTo(width - 10, centerY)
      ctx.stroke()
      
      // 绘制中心竖线
      ctx.beginPath()
      ctx.moveTo(centerX, 10)
      ctx.lineTo(centerX, height - 10)
      ctx.stroke()
      
      // 绘制左上到右下的对角线
      ctx.beginPath()
      ctx.moveTo(10, 10)
      ctx.lineTo(width - 10, height - 10)
      ctx.stroke()
      
      // 绘制右上到左下的对角线
      ctx.beginPath()
      ctx.moveTo(width - 10, 10)
      ctx.lineTo(10, height - 10)
      ctx.stroke()
      
      // 恢复实线样式（避免影响后续绘制）
      ctx.setLineDash([])
      
      // 4. 绘制四个角的辅助点（可选，增强书法字帖效果）
      ctx.fillStyle = '#D4AF37'
      const cornerRadius = 3
      const cornerOffset = 15
      
      // 左上角
      ctx.beginPath()
      ctx.arc(cornerOffset, cornerOffset, cornerRadius, 0, Math.PI * 2)
      ctx.fill()
      
      // 右上角
      ctx.beginPath()
      ctx.arc(width - cornerOffset, cornerOffset, cornerRadius, 0, Math.PI * 2)
      ctx.fill()
      
      // 左下角
      ctx.beginPath()
      ctx.arc(cornerOffset, height - cornerOffset, cornerRadius, 0, Math.PI * 2)
      ctx.fill()
      
      // 右下角
      ctx.beginPath()
      ctx.arc(width - cornerOffset, height - cornerOffset, cornerRadius, 0, Math.PI * 2)
      ctx.fill()
      
      // 恢复绘制样式为黑色笔触（确保用户绘制时使用黑色）
      ctx.strokeStyle = '#000000'
      ctx.lineWidth = 4
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    },
    /**
     * 将显示用画布（米色米字格）转为推理用黑字白底图，仅上传时使用
     */
    canvasToBlackWhiteBlob(sourceCanvas) {
      const w = sourceCanvas.width
      const h = sourceCanvas.height
      const off = document.createElement('canvas')
      off.width = w
      off.height = h
      const ctx = off.getContext('2d')
      ctx.drawImage(sourceCanvas, 0, 0)
      const imageData = ctx.getImageData(0, 0, w, h)
      const data = imageData.data
      const threshold = 230
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i]
        const g = data[i + 1]
        const b = data[i + 2]
        const luminance = (r + g + b) / 3
        const v = luminance >= threshold ? 255 : 0
        data[i] = data[i + 1] = data[i + 2] = v
        data[i + 3] = 255
      }
      ctx.putImageData(imageData, 0, 0)
      return new Promise((resolve, reject) => {
        off.toBlob((blob) => {
          if (blob) resolve(blob)
          else reject(new Error('黑字白底转换失败'))
        }, 'image/png')
      })
    },
    async uploadCanvas() {
      const canvas = this.$refs.drawingCanvas
      if (!canvas || !this.hasDrawing) {
        throw new Error('请先绘制骨架图')
      }
      const blob = await this.canvasToBlackWhiteBlob(canvas)
      const formData = new FormData()
      formData.append('file', blob, 'skeleton.png')
      return this.$http.post(this.uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(response => {
        if (response.data.success) {
          this.uploadedFileName = response.data.fileName
          return response.data.fileName
        }
        throw new Error(response.data.message || '上传失败')
      })
    },
    async generateImage() {
      if (!this.hasDrawing) {
        this.$message.warning('请先绘制骨架图')
        return
      }
      
      this.generating = true
      this.resultImage = ''
      this.showProgressDialog = true
      this.progressPercentage = 0
      this.currentStage = '铺纸研墨'
      this.progressTip = '准备生成环境...'
      this.userTaskStatus = 'waiting' // 初始状态为等待
      this.tasksAhead = 0
      
      // 启动进度模拟（只有在执行状态时才需要）
      // this.startProgressSimulation()
      
      try {
        let fileName = this.uploadedFileName
        if (!fileName || this.hasDrawing) {
          fileName = await this.uploadCanvas()
        }
        
        const CancelToken = this.$http.CancelToken
        this.cancelTokenSource = CancelToken.source()
        
        // 不传递参数，让 FastAPI 使用风格特定的默认值
        console.log('当前选择的风格:', this.styleType)
        const requestData = {
          styleType: this.styleType,
          fileName: fileName
        }
        console.log('发送的请求数据:', requestData)
        
        // 先提交任务获取taskId
        const submitResponse = await this.$http.post('/image/submit', requestData, {
          cancelToken: this.cancelTokenSource.token
        })
        
        // 调试：打印响应
        console.log('提交任务响应:', submitResponse.data)
        
        // 保存当前任务ID
        if (submitResponse.data && submitResponse.data.success && submitResponse.data.taskId) {
          this.currentTaskId = submitResponse.data.taskId
          console.log('任务已提交，taskId:', this.currentTaskId)
          // 任务提交后，立即查询一次状态
          // 状态会在队列监控中更新
        } else {
          console.error('提交任务失败，响应:', submitResponse.data)
          throw new Error('提交任务失败: ' + (submitResponse.data?.message || '未知错误'))
        }
        
        // 等待任务完成并获取结果（通过轮询）
        const taskId = this.currentTaskId
        let pollCount = 0
        const maxPollCount = 600 // 最多轮询10分钟
        const pollInterval = 1000 // 每1秒轮询一次
        
        while (pollCount < maxPollCount) {
          try {
            // 查询任务进度
            const progressResponse = await this.$http.get(`/image/progress/${taskId}`)
            if (progressResponse.data && progressResponse.data.success) {
              const status = progressResponse.data.status
              
              if (status === 'completed') {
                // 任务完成，获取结果
                const resultResponse = await this.$http.get(`/image/result/${taskId}`)
                if (resultResponse.data && resultResponse.data.success && resultResponse.data.image) {
                  this.resultImage = resultResponse.data.image
                  
                  // 完成进度到100%
                  this.completeProgress()
                  
                  // 延迟关闭弹窗，让用户看到完成状态
                  setTimeout(() => {
                    this.showProgressDialog = false
                    this.$message.success('图片生成成功!')
                  }, 800)
                  
                  // 历史记录已在后端自动保存，重新加载第一页
                  this.loadHistory(1)
                  break
                } else {
                  throw new Error('获取生成结果失败')
                }
              } else if (status === 'failed') {
                const errorMsg = progressResponse.data.error || '生成失败'
                this.showProgressDialog = false
                
                // 检查是否是NSFW错误
                if (errorMsg.includes('NSFW') || errorMsg.includes('nsfw')) {
                  // NSFW错误，显示友好的提示信息
                  this.$message.warning('检测到不当内容，请尝试使用不同的输入或参数')
                } else {
                  // 其他错误
                  this.$message.error(errorMsg)
                }
                
                // 直接退出循环，不再继续轮询
                throw new Error(errorMsg)
              } else if (status === 'cancelled') {
                this.showProgressDialog = false
                this.$message.info('生成已取消')
                throw new Error('任务已取消')
              }
              // 如果状态是 processing 或 queued，继续轮询
            }
            
            // 等待一段时间后继续轮询
            await new Promise(resolve => setTimeout(resolve, pollInterval))
            pollCount++
          } catch (error) {
            if (this.$http.isCancel && this.$http.isCancel(error)) {
              throw error
            }
            // 如果是任务失败、取消或获取结果失败，直接抛出，不再继续轮询
            if (error.message && (
              error.message.includes('获取') || 
              error.message.includes('失败') || 
              error.message.includes('取消') ||
              error.message.includes('NSFW') ||
              error.message.includes('nsfw')
            )) {
              throw error
            }
            // 其他错误，继续轮询
            await new Promise(resolve => setTimeout(resolve, pollInterval))
            pollCount++
          }
        }
        
        if (pollCount >= maxPollCount) {
          throw new Error('任务超时')
        }
        
      } catch (error) {
        this.stopProgressSimulation()
        this.showProgressDialog = false
        
        if (this.$http.isCancel && this.$http.isCancel(error)) {
          this.$message.info('生成已取消')
        } else if (error.message && (error.message.includes('cancel') || error.message.includes('Cancel'))) {
          this.$message.info('生成已取消')
        } else {
          // 检查是否是NSFW错误（已经在轮询中显示过，这里不再重复显示）
          if (error.message && (error.message.includes('NSFW') || error.message.includes('nsfw'))) {
            // NSFW错误已经在轮询中显示过，这里不再重复显示
            // 只记录日志
            console.warn('NSFW错误已处理:', error.message)
          } else if (error.message && (error.message.includes('上传') || error.message.includes('绘制'))) {
            this.$message.error('上传失败: ' + (error.message || '未知错误'))
          } else if (error.message && !error.message.includes('失败')) {
            // 只有在错误消息中不包含"失败"时才显示（避免重复显示）
            // 如果错误消息已经包含"失败"，说明已经在轮询中显示过了
            this.$message.error('生成失败: ' + (error.response?.data?.message || error.message || '未知错误'))
          }
        }
      } finally {
        this.generating = false
        this.cancelTokenSource = null
        this.currentTaskId = null
        this.userTaskStatus = 'no_task'
        this.tasksAhead = 0
      }
    },
    
    startProgressSimulation() {
      let currentProgress = 0
      const totalDuration = 60000 // 预计总时长60秒
      const updateInterval = 200 // 每200ms更新一次
      const incrementPerUpdate = (95 / totalDuration) * updateInterval // 模拟到95%
      
      this.progressTimer = setInterval(() => {
        if (currentProgress < 95) {
          currentProgress += incrementPerUpdate
          this.progressPercentage = Math.min(Math.floor(currentProgress), 95)
          
          // 根据进度更新阶段
          for (let i = 0; i < this.stages.length; i++) {
            const stage = this.stages[i]
            if (this.progressPercentage >= stage.range[0] && this.progressPercentage < stage.range[1]) {
              this.currentStage = stage.name
              this.progressTip = stage.tip
              break
            }
          }
        }
      }, updateInterval)
    },
    
    stopProgressSimulation() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    
    completeProgress() {
      this.stopProgressSimulation()
      this.progressPercentage = 100
      this.currentStage = '画龙点睛'
      this.progressTip = '大功告成！'
    },
    cancelGenerate() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('用户取消生成')
      }
      this.stopProgressSimulation()
      this.showProgressDialog = false
      this.generating = false
      this.currentTaskId = null
    },
    
    resetToCanvas() {
      this.resultImage = ''
      this.clearCanvas()
      this.$message.info('已清空画布，可以重新绘制')
    },
    handleHistoryCommand(command) {
      // 下拉菜单命令处理（如果需要）
    },
    viewHistoryImage(item) {
      this.resultImage = item.image
      this.$message.success('已加载历史图片')
    },
    clearHistory() {
      this.$confirm('确定要清空所有历史记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        if (this.historyIsMock) {
          this.mockHistoryData = []
          this.historyList = []
          this.historyTotal = 0
          this.historyPage = 1
          this.$message.success('历史记录已清空（演示数据）')
          return
        }
        try {
          const response = await this.$http.delete('/image/history')
          if (response.data.success) {
            this.fullHistoryCache = []
            this.historyList = []
            this.historyTotal = 0
            this.historyPage = 1
            this.$message.success('历史记录已清空')
          } else {
            this.$message.error(response.data.message || '清空失败')
          }
        } catch (error) {
          this.$message.error('清空历史记录失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        }
      }).catch(() => {})
    },
    deleteHistoryItem(item) {
      this.$confirm('确定要删除这条历史记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        if (this.historyIsMock) {
          this.mockHistoryData = this.mockHistoryData.filter(h => h.id !== item.id)
          const size = this.historyPageSize
          const total = this.mockHistoryData.length
          const from = (this.historyPage - 1) * size
          const to = Math.min(from + size, total)
          this.historyList = total ? this.mockHistoryData.slice(from, to) : []
          this.historyTotal = total
          if (this.historyList.length === 0 && total > 0 && this.historyPage > 1) {
            this.loadHistory(this.historyPage - 1)
          }
          this.$message.success('删除成功（演示数据）')
          return
        }
        try {
          const response = await this.$http.delete(`/image/history/${item.id}`)
          if (response.data.success) {
            this.$message.success('删除成功')
            await this.loadHistory()
          } else {
            this.$message.error(response.data.message || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除历史记录失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        }
      }).catch(() => {})
    },
    /** 本地测试：从 outputs/history.json 加载历史（开发服务器已配置 /outputs 指向该目录） */
    async loadLocalHistoryJson() {
      try {
        const res = await fetch('/outputs/history.json')
        if (!res.ok) return []
        const data = await res.json()
        const list = Array.isArray(data) ? data : []
        list.forEach(item => {
          if (item.image && !item.image.startsWith('data:') && !item.image.startsWith('http') && !item.image.startsWith('/')) {
            item.image = '/outputs/' + item.image
          }
        })
        return list
      } catch (e) {
        return []
      }
    },
    /** 生成本地 mock 历史数据（无 history.json 时回退），25 条均使用 outputs 下的同一张图 */
    generateMockHistory(count) {
      const styles = ['dry', 'wet', 'strong', 'light']
      // 开发服务器已配置将 ../outputs 映射到 /outputs，本地测试时使用该图
      const mockImageUrl = '/outputs/535377bc-1d15-4722-b277-74e80dad28b6.png'
      const now = Date.now()
      const list = []
      for (let i = 0; i < count; i++) {
        list.push({
          id: 'mock-' + i,
          image: mockImageUrl,
          timestamp: now - i * 3600000,
          styleType: styles[i % styles.length]
        })
      }
      return list
    },
    async loadHistory(page) {
      const p = page != null ? page : this.historyPage
      const size = this.historyPageSize
      // 已有全量缓存且仅翻页时，直接切片，避免重复请求
      if (this.fullHistoryCache.length > 0 && this.historyTotal > size) {
        const from = (p - 1) * size
        const to = Math.min(from + size, this.fullHistoryCache.length)
        this.historyList = this.fullHistoryCache.slice(from, to)
        this.historyPage = p
        return
      }
      try {
        const response = await this.$http.get('/image/history', {
          params: { page: p, size }
        })
        this.historyIsMock = false
        if (response.data.success && response.data.data) {
          const data = response.data.data
          const total = response.data.total
          // 兼容：若后端未分页（一次返回全部且条数 > size），前端自己做分页
          const likelyUnpaginated = data.length > size && (total === undefined || total === data.length)
          if (likelyUnpaginated) {
            this.fullHistoryCache = data
            this.historyTotal = data.length
            const from = (p - 1) * size
            const to = Math.min(from + size, data.length)
            this.historyList = data.slice(from, to)
          } else {
            this.fullHistoryCache = []
            this.historyList = data
            this.historyTotal = total !== undefined ? total : data.length
          }
          this.historyPage = p
          if (this.historyList.length === 0 && this.historyTotal > 0 && p > 1) {
            this.loadHistory(p - 1)
          }
        } else {
          this.fullHistoryCache = []
          this.historyList = []
          this.historyTotal = 0
        }
      } catch (error) {
        // 后端未启动：优先使用 outputs/history.json，否则用生成的 mock 数据
        this.historyIsMock = true
        const fromFile = await this.loadLocalHistoryJson()
        if (fromFile.length > 0) {
          this.mockHistoryData = fromFile
        } else if (this.mockHistoryData.length === 0) {
          this.mockHistoryData = this.generateMockHistory(25)
        }
        const total = this.mockHistoryData.length
        const from = (p - 1) * size
        const to = Math.min(from + size, total)
        this.historyList = total ? this.mockHistoryData.slice(from, to) : []
        this.historyPage = p
        this.historyTotal = total
        if (this.historyList.length === 0 && total > 0 && p > 1) {
          this.loadHistory(p - 1)
        }
      }
    },
    onHistoryPageChange(page) {
      this.loadHistory(page)
    },
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) { // 1分钟内
        return '刚刚'
      } else if (diff < 3600000) { // 1小时内
        return Math.floor(diff / 60000) + '分钟前'
      } else if (diff < 86400000) { // 1天内
        return Math.floor(diff / 3600000) + '小时前'
      } else {
        const month = date.getMonth() + 1
        const day = date.getDate()
        const hour = date.getHours()
        const minute = date.getMinutes()
        return `${month}-${day} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      }
    },
    getStyleName(styleType) {
      const styleMap = {
        'dry': '干',
        'wet': '湿',
        'strong': '浓',
        'light': '淡'
      }
      return styleMap[styleType] || styleType
    },
    downloadResult() {
      if (!this.resultImage) return
      
      // 创建下载链接
      const link = document.createElement('a')
      link.href = this.resultImage
      link.download = 'generated-image.png'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    startQueueMonitor() {
      // 停止之前的监控（如果存在）
      this.stopQueueMonitor()
      
      // 每2秒查询一次队列信息和当前用户任务状态
      this.queueMonitorTimer = setInterval(async () => {
        try {
          // 查询队列信息
          const queueResponse = await this.$http.get('/image/queue/info')
          if (queueResponse.data && queueResponse.data.success) {
            const executingTaskId = queueResponse.data.executing_task_id
            const queuedTaskIds = queueResponse.data.queued_task_ids || []
            
            // 查询当前用户任务状态
            let userTaskStatus = null
            try {
              // 调试：打印当前任务ID
              console.log('调试: currentTaskId =', this.currentTaskId)
              const statusResponse = await this.$http.get('/image/task/status', {
                params: this.currentTaskId ? {
                  taskId: this.currentTaskId
                } : {}
              })
              if (statusResponse.data && statusResponse.data.success) {
                userTaskStatus = {
                  status: statusResponse.data.status,
                  message: statusResponse.data.message,
                  taskId: statusResponse.data.task_id
                }
                
                // 检查状态是否发生变化
                const previousStatus = this.userTaskStatus
                this.userTaskStatus = userTaskStatus.status
                
                // 如果是等待状态，计算前方等待的任务数
                if (userTaskStatus.status === 'waiting') {
                  // 从 message 中提取数字，例如 "当前任务在等待队列中，前面还有 2 个任务"
                  const match = userTaskStatus.message.match(/前面还有\s*(\d+)\s*个任务/)
                  if (match) {
                    const aheadCount = parseInt(match[1], 10)
                    this.tasksAhead = aheadCount + 1 // 包括当前任务
                  } else {
                    // 如果无法从 message 中提取，从队列中计算
                    const taskIndex = queuedTaskIds.indexOf(this.currentTaskId)
                    if (taskIndex >= 0) {
                      this.tasksAhead = taskIndex + 1 // 包括当前任务
                    } else {
                      this.tasksAhead = queuedTaskIds.length + 1
                    }
                  }
                  // 等待状态时停止进度条模拟
                  if (previousStatus !== 'waiting') {
                    this.stopProgressSimulation()
                  }
                } else if (userTaskStatus.status === 'executing') {
                  // 执行状态时启动进度条模拟
                  this.tasksAhead = 0
                  if (previousStatus !== 'executing') {
                    this.startProgressSimulation()
                  }
                } else {
                  this.tasksAhead = 0
                  this.stopProgressSimulation()
                }
              } else {
                // 如果响应不成功，也设置默认状态
                userTaskStatus = {
                  status: 'no_task',
                  message: '查询任务状态失败',
                  taskId: null
                }
                this.userTaskStatus = 'no_task'
                this.tasksAhead = 0
              }
            } catch (error) {
              // 查询失败，设置默认状态
              userTaskStatus = {
                status: 'no_task',
                message: '查询任务状态失败: ' + (error.message || '未知错误'),
                taskId: null
              }
              this.userTaskStatus = 'no_task'
              this.tasksAhead = 0
            }
            
            // 打印到控制台
            console.log('========== 队列信息 ==========')
            console.log('1. 正在执行的ID:', executingTaskId || '无')
            console.log('2. 等待的所有ID:', queuedTaskIds.length > 0 ? queuedTaskIds : '无')
            if (queuedTaskIds.length > 0) {
              console.log('   详细列表:', queuedTaskIds)
            }
            console.log('============================')
            
            // 打印当前用户任务状态
            console.log('========== 当前用户任务状态 ==========')
            if (userTaskStatus) {
              console.log('状态:', userTaskStatus.status)
              console.log('说明:', userTaskStatus.message)
              if (userTaskStatus.taskId) {
                console.log('任务ID:', userTaskStatus.taskId)
              }
            } else {
              console.log('状态: 无任务')
              console.log('说明: 当前用户无任务')
            }
            console.log('====================================')
          }
        } catch (error) {
          // 静默失败，不打印错误（避免控制台污染）
          // console.warn('获取队列信息失败:', error)
        }
      }, 2000) // 每2秒查询一次
    },
    stopQueueMonitor() {
      if (this.queueMonitorTimer) {
        clearInterval(this.queueMonitorTimer)
        this.queueMonitorTimer = null
      }
    }
  }
}
</script>

<style scoped>
#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #5C4A37;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #FEF9E7;
  overflow: hidden; /* 防止整体页面滚动 */
}

#app >>> .el-container {
  height: 100vh;
  overflow: hidden; /* 防止容器滚动 */
  display: flex;
  flex-direction: column;
}

.el-header {
  background: linear-gradient(135deg, #D4AF37 0%, #C9A961 100%);
  color: #5C4A37;
  text-align: center;
  line-height: 50px;
  height: 50px !important;
  box-shadow: 0 2px 12px 0 rgba(212, 175, 55, 0.3);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  height: 100%;
}

.el-header h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}

/* 历史记录面板 */
.history-panel {
  padding: 10px 12px;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* 面板本身不滚动 */
  min-height: 0;
  flex: 1;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #D4AF37;
  flex-shrink: 0;
}

.history-header h3 {
  margin: 0;
  color: #8B6914;
  font-size: 16px;
  font-weight: 500;
}

.history-mock-tip {
  font-size: 12px;
  color: #A0826D;
  font-weight: 400;
}

.history-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #A0826D;
  padding: 40px 20px;
  min-height: 0;
  overflow: hidden;
}

.history-empty i {
  font-size: 48px;
  margin-bottom: 10px;
  opacity: 0.5;
}

.history-empty p {
  margin: 0;
  font-size: 14px;
}

.history-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  max-height: 100%;
  flex-shrink: 1;
  padding-right: 4px;
  /* 自定义滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: #D4AF37 #FEF9E7;
}

/* Webkit 浏览器滚动条样式 */
.history-list::-webkit-scrollbar {
  width: 8px;
}

.history-list::-webkit-scrollbar-track {
  background: #FEF9E7;
  border-radius: 4px;
}

.history-list::-webkit-scrollbar-thumb {
  background: #D4AF37;
  border-radius: 4px;
  transition: background 0.3s ease;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background: #C9A961;
}

.history-pagination {
  flex-shrink: 0;
  padding: 8px 0;
  text-align: center;
}

.history-pagination >>> .el-pager li {
  min-width: 28px;
  font-size: 12px;
}

.history-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px;
  background: #FFF8E7;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.history-item:hover {
  background: #F5E6D3;
  border-color: #D4AF37;
  box-shadow: 0 2px 8px 0 rgba(212, 175, 55, 0.3);
}

.history-thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  margin-right: 10px;
  flex-shrink: 0;
}

.history-thumbnail >>> .el-image__inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.history-image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #FFF8E7;
  color: #A0826D;
  font-size: 20px;
}

.history-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.history-time {
  font-size: 12px;
  color: #8B6914;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-style {
  font-size: 14px;
  color: #8B6914;
  font-weight: 500;
}

.history-delete {
  flex-shrink: 0;
  padding: 4px 8px;
  color: #C97A5E;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.history-delete:hover {
  opacity: 1;
}

.history-pagination {
  flex-shrink: 0;
  padding: 10px 0 4px;
  text-align: center;
}

.history-pagination >>> .el-pager li {
  min-width: 28px;
  font-size: 12px;
}

.el-main {
  padding: 0;
  flex: 1;
  overflow: hidden; /* 主容器不滚动 */
  background-color: #FEF9E7;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 50px); /* 视口高度 - header高度 */
  max-height: calc(100vh - 50px);
}

.main-layout {
  display: flex;
  height: 100%;
  max-height: 100%;
  gap: 0;
  overflow: hidden; /* 布局容器不滚动 */
  flex: 1;
  min-height: 0;
}

/* 左侧风格选择栏 */
.left-sidebar {
  width: 200px;
  background: #FEF9E7;
  border-right: 1px solid #D4AF37;
  overflow: visible; /* 固定显示，不滚动 */
  flex-shrink: 0;
}

.style-selection-sidebar {
  padding: 8px 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar-title {
  margin: 0 0 8px 0;
  color: #8B6914;
  font-size: 15px;
  font-weight: 500;
  text-align: center;
}

.style-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 右侧历史记录栏 */
.right-sidebar {
  width: 280px;
  background: #FEF9E7;
  border-left: 1px solid #D4AF37;
  overflow: hidden; /* 不滚动，让内部元素滚动 */
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  position: relative;
  min-height: 0; /* 允许flex子元素收缩 */
}

/* 中间画板区域 */
.center-canvas {
  flex: 1;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  height: calc(100vh - 50px); /* 视口高度 - header高度 */
  max-height: calc(100vh - 50px);
  align-self: flex-start;
}

/* 风格选择项（竖向） */
.style-item-vertical {
  position: relative;
  border: 2px solid #D4AF37;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #FFF8E7;
  overflow: hidden;
}

.style-item-vertical:hover {
  border-color: #C9A961;
  box-shadow: 0 2px 8px 0 rgba(212, 175, 55, 0.4);
}

.style-item-vertical.active {
  border-color: #8B6914;
  background: #F5E6D3;
  box-shadow: 0 2px 8px 0 rgba(139, 105, 20, 0.5);
}

.style-image-container-vertical {
  width: 100%;
  padding-top: 80%; /* 略缩小高度，便于一次展示所有风格 */
  background: #FFF8E7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 6px;
  position: relative;
}

.style-image-vertical {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain; /* 改为contain，完整显示图片 */
}

.style-label-vertical {
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  color: #5C4A37;
  padding: 4px 0;
}

.style-item-vertical.active .style-label-vertical {
  color: #8B6914;
  font-weight: 600;
}

.style-check-vertical {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 18px;
  color: #8B6914;
  background: #FFF8E7;
  border-radius: 50%;
  padding: 2px;
}

.generate-section-sidebar {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #D4AF37;
}

/* 中间画板卡片 */
.canvas-card {
  width: 100%;
  max-width: 800px;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #FFF8E7;
  border: 1px solid #D4AF37;
  overflow: hidden;
}

.canvas-card >>> .el-card__header {
  padding: 6px 15px 2px 15px;
}

.canvas-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 15px 10px 15px;
  overflow: hidden;
  min-height: 0;
  height: 100%;
  justify-content: flex-start;
}

.canvas-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-height: 0;
  flex-shrink: 0;
  height: calc(100% - 50px - 35px); /* 父容器高度 - 按钮区域高度 - footer高度（减少） */
  max-height: calc(100% - 50px - 35px);
  overflow: hidden;
  position: relative;
  margin-bottom: 0;
}

.drawing-canvas {
  border: 2px solid #D4AF37;
  border-radius: 8px;
  cursor: crosshair;
  background: #FEF9E7;
  touch-action: none;
  box-shadow: 0 2px 12px 0 rgba(212, 175, 55, 0.2);
  max-width: 100%;
  max-height: 100%;
}

.canvas-actions {
  margin-top: 0;
  margin-bottom: 0;
  display: flex;
  gap: 10px;
  justify-content: center;
  padding: 4px 0;
  border-top: 1px solid #D4AF37;
  flex-shrink: 0;
  min-height: 45px;
  height: 45px;
  max-height: 45px;
}

.canvas-footer {
  padding: 4px 15px;
  text-align: center;
  border-top: 1px solid #D4AF37;
  flex-shrink: 0;
  background: #FFF8E7;
  margin-top: 0;
}

.copyright-text {
  margin: 0;
  font-size: 12px;
  color: #8B6914;
  line-height: 1.4;
}

/* 结果图片容器 */
.result-image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.result-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(212, 175, 55, 0.3);
  border: 2px solid #D4AF37;
}

.result-image >>> .el-image__inner {
  border-radius: 8px;
}

.image-error-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #FFF8E7;
  color: #A0826D;
  font-size: 48px;
}

/* 进度弹窗样式 */
.progress-dialog >>> .el-dialog {
  border-radius: 12px;
}

.progress-dialog >>> .el-dialog__header {
  padding: 25px 30px 15px;
  border-bottom: 1px solid #D4AF37;
  background: #FFF8E7;
}

.progress-dialog >>> .el-dialog__body {
  padding: 30px;
  background: #FEF9E7;
}

.progress-dialog >>> .el-dialog__footer {
  padding: 15px 30px 25px;
  border-top: 1px solid #D4AF37;
  background: #FFF8E7;
}

.dialog-title {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  color: #5C4A37;
}

.dialog-title i {
  margin-right: 10px;
  font-size: 20px;
  color: #D4AF37;
  animation: rotating 2s linear infinite;
}

.progress-content {
  text-align: center;
}

.progress-stage {
  font-size: 24px;
  font-weight: 600;
  color: #8B6914;
  margin-bottom: 20px;
  letter-spacing: 2px;
}

.progress-tip {
  margin-top: 15px;
  font-size: 14px;
  color: #8B6914;
}

.waiting-content {
  text-align: center;
  padding: 30px 20px;
}

.waiting-icon {
  font-size: 48px;
  color: #FF9800;
  margin-bottom: 20px;
  animation: pulse 2s ease-in-out infinite;
}

.waiting-message {
  color: #856404;
}

.waiting-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #856404;
}

.waiting-count {
  font-size: 24px;
  font-weight: 700;
  color: #FF9800;
  margin-top: 10px;
}

.queue-info {
  margin-top: 20px;
  margin-bottom: 15px;
  padding: 20px;
  background: linear-gradient(135deg, #FFF3CD 0%, #FFE69C 100%);
  border: 2px solid #FFC107;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  color: #856404;
  box-shadow: 0 2px 8px 0 rgba(255, 193, 7, 0.3);
  min-height: 60px;
}

.queue-info i {
  margin-right: 10px;
  font-size: 24px;
  color: #FF9800;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.dialog-footer {
  text-align: center;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-layout {
    flex-direction: column;
  }
  
  .left-sidebar,
  .right-sidebar {
    width: 100%;
    max-height: 300px;
  }
  
  .center-canvas {
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .left-sidebar {
    width: 100%;
  }
  
  .right-sidebar {
    width: 100%;
  }
  
  .style-list {
    flex-direction: row;
    overflow-x: auto;
  }
  
  .style-item-vertical {
    min-width: 120px;
  }
  
  .progress-dialog {
    width: 90% !important;
  }
}
</style>
